/**
 * Activity hexagons (MapActivityMarker.vue): the exact Path2D outline filled by
 * state/status colour, with the sequence number centred, mirrored-activity black
 * stroke, and hover/selection rings. Selection takes visual precedence over
 * hover. Operates on the visible activity set from the content frame.
 */
import { activityHexagonColors, cssVar } from '../../draw/palette';
import { hexPath } from '../../draw/shapes';
import { withGroupOpacity } from '../../engine/groupOpacity';
import { layoutY } from '../../layout/activityLayout';
import { makeMirroredStroke, type MirroredStroke } from './mirroredStroke';
import type { SceneContext } from '../../engine/SceneContext';
import type { MapActivity } from '@/store/ioStore';

export function drawHexagons(
  ctx: CanvasRenderingContext2D,
  scene: SceneContext,
): void {
  const { map } = scene;
  const activities = scene.content?.visible ?? [];

  // Sequence-number text settings, set once (constant font). Centre on the true
  // glyph box rather than the `middle` baseline (which renders digits a little
  // low) so the number sits dead-centre in the hexagon, matching the SVG's
  // dominant-baseline="central". Digits share vertical metrics, so one probe
  // measurement applies to every hexagon.
  ctx.font = '800 13px Roboto, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  const probe = ctx.measureText('0');
  const numberCY =
    (probe.actualBoundingBoxAscent - probe.actualBoundingBoxDescent) / 2;

  // Source-project → mirror-outline colour, resolved once per frame.
  const mirroredStroke = makeMirroredStroke(scene);

  // Filter dimming: activities that don't match an active filter (or every
  // activity in milestone-overlay mode) render at 0.2 — like the SVG
  // `.activity-group.shadowed { opacity: 0.2 }`. That's GROUP opacity: each
  // dimmed hexagon must flatten (fill + rings + mirror stroke + number) before it
  // composites at 0.2, else the overlapping strokes darken each other. So draw
  // the un-dimmed hexagons straight to the layer and the dimmed ones through one
  // batched group-opacity pass.
  const collapsed = scene.layout?.collapsedIds;
  const dimmed: MapActivity[] = [];
  for (const activity of activities) {
    // Members of a collapsed occlusion group render as one marker
    // (GroupMarkersPainter) instead of their own hexagons.
    if (collapsed?.has(activity.id)) {
      continue;
    }
    if (map.isShadowedActivityId(activity.id)) {
      dimmed.push(activity);
    } else {
      drawHexagon(ctx, scene, activity, numberCY, mirroredStroke);
    }
  }
  if (dimmed.length) {
    withGroupOpacity(ctx, 0.2, (g) => {
      g.font = '800 13px Roboto, sans-serif';
      g.textAlign = 'center';
      g.textBaseline = 'alphabetic';
      for (const activity of dimmed) {
        drawHexagon(g, scene, activity, numberCY, mirroredStroke);
      }
    });
  }
}

/** Draw a single hexagon at full opacity (dimming is applied by the caller). */
function drawHexagon(
  ctx: CanvasRenderingContext2D,
  scene: SceneContext,
  activity: MapActivity,
  numberCY: number,
  mirroredStroke: MirroredStroke,
): void {
  const { map } = scene;
  const x = scene.screenX(map.activityPos(activity));
  const y = layoutY(scene, activity.id);
  const statusView = map.isHidingActivityColors;
  const colors = activityHexagonColors(
    activity,
    statusView,
    map.activityFinalColors(activity.id),
  );

  const selected =
    map.selectedId === activity.id || map.selectedIds.includes(activity.id);
  // A move in progress leaves this activity behind — the user may not move it.
  const leftBehind = map.leftBehindActivityIds.includes(activity.id);
  const hovered = map.hoveredActivity === activity.id;
  const todoDragTarget = map.todoDragHoveredActivityId === activity.id;

  ctx.save();
  ctx.translate(x, y);

  // Hover ring (SVG `[data-hovered] .hexagon`: #ccc, width 8). Selection takes
  // visual precedence, so only draw hover when not selected.
  if (hovered && !selected && !leftBehind) {
    ctx.save();
    ctx.lineWidth = 8;
    ctx.strokeStyle = '#cccccc';
    ctx.lineJoin = 'round';
    ctx.stroke(hexPath);
    ctx.restore();
  }

  // Selection ring (var(--color-bluelight-500), width 8 @ 0.6). The 0.6 stacks
  // with the group alpha when the hexagon is dimmed (SVG: ring 0.6 × group 0.2).
  // Red while a move leaves the activity behind, at 0.9 so it reads as red
  // rather than salmon.
  if (selected || leftBehind) {
    ctx.save();
    ctx.globalAlpha = leftBehind ? 0.9 : 0.6;
    ctx.lineWidth = 8;
    ctx.strokeStyle = leftBehind
      ? cssVar('--color-error-500', '#F04438')
      : cssVar('--color-bluelight-500', '#0BA5EC');
    ctx.stroke(hexPath);
    ctx.restore();
  }

  // Todo-drag target ring (SVG `.hexagon.todo-drag-target`: var(--color-success-400),
  // width 10 @ 0.9, with a success-300 glow). Highlights the activity a dragged
  // todo would attach to; drawn last so it reads on top of any hover/selection ring.
  if (todoDragTarget) {
    ctx.save();
    ctx.globalAlpha = 0.9;
    ctx.lineWidth = 10;
    ctx.lineJoin = 'round';
    ctx.strokeStyle = cssVar('--color-success-400', 'rgb(50, 213, 131)');
    ctx.shadowColor = cssVar('--color-success-300', 'rgb(108, 233, 166)');
    ctx.shadowBlur = 10;
    ctx.stroke(hexPath);
    ctx.restore();
  }

  // Search-hovered ring (SVG `.hexagon.search-hovered`: var(--color-primary-400),
  // width 10 @ 0.8, with a primary-300 glow). Driven by hovering a search result
  // (mapStore.hoveredSearchActivity), independent of pointer hover/selection.
  if (map.hoveredSearchActivity === activity.id) {
    ctx.save();
    ctx.globalAlpha = 0.8;
    ctx.lineWidth = 10;
    ctx.lineJoin = 'round';
    ctx.strokeStyle = cssVar('--color-primary-400', '#6F8DB6');
    ctx.shadowColor = cssVar('--color-primary-300', '#86A2C8');
    ctx.shadowBlur = 8;
    ctx.stroke(hexPath);
    ctx.restore();
  }

  // Mirrored activities get a width-4 outline in their source project's palette
  // colour (MapActivityMarker `strokeColor`), falling back to black — and black in
  // status view, where the SVG drops the inline stroke.
  if (activity.mirrored) {
    ctx.lineWidth = 4;
    ctx.strokeStyle = mirroredStroke(activity.sourceProjectId, statusView);
    ctx.stroke(hexPath);
  }

  ctx.fillStyle = colors.fill;
  ctx.fill(hexPath);

  ctx.fillStyle = colors.text;
  ctx.fillText(String(activity.sequence), 0, numberCY);

  ctx.restore();
}
